/**
 * Scoring utility functions for qualification detail views.
 * Calculates section-level scores and determines score status for highlighting.
 */

import { QualificationSection, QualificationQuestion } from '../../lib/api';
import { ResponseMap, SectionScoreResult, ScoreStatus } from './types';

/**
 * Calculate the score for a single section based on responses.
 * Only includes questions that are marked as scored.
 */
export function calculateSectionScore(
  section: QualificationSection,
  responses: ResponseMap
): SectionScoreResult {
  const questions = section.questions || [];

  let scoreAwarded = 0;
  let maxScore = 0;

  questions.forEach((question: QualificationQuestion) => {
    if (question.is_scored) {
      maxScore += Number(question.max_score) || 0;
      const response = responses[question.id];
      if (response?.score_awarded !== undefined && response.score_awarded !== null) {
        scoreAwarded += Number(response.score_awarded);
      }
    }
  });

  const percentage = maxScore > 0 ? (scoreAwarded / maxScore) * 100 : 0;
  const weight = Number(section.weight) || 0;
  const weightedContribution = maxScore > 0 ? (scoreAwarded / maxScore) * weight : 0;

  return {
    sectionId: section.id,
    sectionTitle: section.title,
    scoreAwarded,
    maxScore,
    percentage,
    weight,
    weightedContribution,
  };
}

/**
 * Determine the score status level for visual highlighting.
 * @param scoreAwarded - The actual score received
 * @param maxScore - The maximum possible score
 * @returns ScoreStatus with level and CSS classes
 */
export function getScoreStatus(
  scoreAwarded: number | undefined | null,
  maxScore: number | string
): ScoreStatus {
  const numericMaxScore = Number(maxScore) || 0;

  // If not scored or no max score, return neutral
  if (numericMaxScore === 0 || scoreAwarded === undefined || scoreAwarded === null) {
    return {
      level: 'neutral',
      borderClass: '',
      bgClass: '',
      textClass: 'text-muted-foreground',
    };
  }

  const percentage = (Number(scoreAwarded) / numericMaxScore) * 100;

  if (percentage >= 75) {
    return {
      level: 'good',
      borderClass: 'border-green-300',
      bgClass: 'bg-green-50',
      textClass: 'text-green-700',
    };
  } else if (percentage >= 25) {
    return {
      level: 'partial',
      borderClass: 'border-amber-300',
      bgClass: 'bg-amber-50',
      textClass: 'text-amber-700',
    };
  } else {
    return {
      level: 'failed',
      borderClass: 'border-red-300',
      bgClass: 'bg-red-50',
      textClass: 'text-red-700',
    };
  }
}

/**
 * Get color classes for section score display based on pass threshold.
 */
export function getSectionScoreColor(percentage: number, passScore: number): string {
  if (percentage >= passScore) {
    return 'text-green-600';
  } else if (percentage >= passScore * 0.5) {
    return 'text-amber-600';
  } else {
    return 'text-red-600';
  }
}

/**
 * Get progress bar color class based on score percentage and pass threshold.
 */
export function getProgressBarColor(percentage: number, passScore: number): string {
  if (percentage >= passScore) {
    return '[&>div]:bg-green-500';
  } else if (percentage >= passScore * 0.5) {
    return '[&>div]:bg-amber-500';
  } else {
    return '[&>div]:bg-red-500';
  }
}
